import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { DREAM_CATEGORIES } from '../../data/fortuneData';
import { getAllDreamCategories } from '../../data/dreamData';
import DreamCard from '../../components/fortune/DreamCard';
import FortuneContent from '../../components/fortune/FortuneContent';
import AdBanner from '../../components/AdBanner';
import SEO from '../../components/SEO';
import { interpretDream } from '../../services/api';
import './FortuneCategoryPage.css';

function DreamPage() {
  const [fortune, setFortune] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dreamText, setDreamText] = useState('');

  const scrollToResult = () => {
    setTimeout(() => {
      const element = document.querySelector('.fortune-content');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleInterpret = async (e) => {
    e.preventDefault();
    const trimmed = dreamText.trim();
    if (trimmed.length < 5) {
      alert('꿈 내용을 5자 이상 입력해주세요.');
      return;
    }
    if (trimmed.length > 200) {
      alert('꿈 내용은 200자 이하로 입력해주세요.');
      return;
    }

    setLoading(true);
    try {
      const response = await interpretDream(trimmed);
      if (response.success) {
        setFortune({
          title: '🌙 나의 꿈 해몽',
          overall: response.data.interpretation,
          advice: response.data.advice,
          omen: response.data.omen,
          luckyNumbers: response.data.luckyNumbers,
          luckyColor: response.data.luckyColor,
          luckyDirection: response.data.luckyDirection
        });
        scrollToResult();
      }
    } catch (error) {
      console.error('Error interpreting dream:', error);
      alert('꿈 해석에 실패했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setLoading(false);
    }
  };

  const allCategories = getAllDreamCategories();

  const itemListJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: '꿈해몽 카테고리',
    description: '동물, 자연, 사람, 사물, 행동, 장소, 신체, 죽음 등 8가지 카테고리별 꿈해몽',
    numberOfItems: allCategories.length,
    itemListElement: allCategories.map((cat, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: `${cat.name} 해몽`,
      url: `https://lottounse.kr/fortune/dream/${cat.id}`,
      description: cat.seoDescription,
    })),
  };

  return (
    <div className="fortune-category-page">
      <SEO
        title="AI 꿈해몽 - 인공지능 꿈 해석 & GPT 꿈풀이"
        description="AI 꿈해몽! 인공지능 GPT가 분석하는 꿈 해석. 동물꿈, 뱀꿈, 돼지꿈, 떨어지는 꿈 등 AI가 알려주는 꿈의 의미와 행운 번호를 확인하세요."
        keywords="AI 꿈해몽, 인공지능 꿈해몽, GPT 꿈해몽, AI 꿈 해석, 인공지능 꿈풀이, 꿈해몽, 꿈풀이, 꿈해석, 뱀꿈, 돼지꿈, 용꿈, 이빨꿈, AI 행운 번호"
        url="https://lottounse.kr/fortune/dream"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(itemListJsonLd)}</script>
      </Helmet>

      <div className="category-header dream-header">
        <h1>🌙 꿈해몽</h1>
        <p>꿈에서 본 것의 의미를 알아보세요</p>
      </div>

      <AdBanner />

      <div className="category-content">
        <p className="seo-intro">꿈해몽은 꿈에서 본 상징을 해석하여 길흉화복을 판단하는 전통 문화입니다. 동물, 자연, 사람, 사물 등 {allCategories.length}개 카테고리, {allCategories.reduce((sum, c) => sum + c.subjects.length, 0)}개 주제의 꿈 해석을 확인해보세요.</p>
        <h2 className="section-title">📂 카테고리별 꿈해몽</h2>
        <div className="cards-grid">
          {DREAM_CATEGORIES.map((category) => (
            <DreamCard
              key={category.id}
              category={category}
            />
          ))}
        </div>

        <AdBanner />

        <form className="dream-input-section" onSubmit={handleInterpret}>
          <h3>💭 AI 꿈 해몽 - 꿈 내용을 입력하세요</h3>
          <div className="dream-input-wrapper">
            <textarea
              className="dream-textarea"
              value={dreamText}
              onChange={(e) => setDreamText(e.target.value)}
              placeholder="예: 하늘을 나는 꿈을 꿨어요"
              maxLength={200}
              rows={3}
            />
            <div className="dream-input-footer">
              <span className="char-count">{dreamText.length}/200</span>
              <button
                type="submit"
                className="dream-submit-btn"
                disabled={loading || dreamText.trim().length < 5}
              >
                🔮 해몽하기
              </button>
            </div>
          </div>
        </form>

        {loading && (
          <div className="loading-section">
            <p>꿈을 해석하는 중...</p>
          </div>
        )}

        {fortune && !loading && (
          <>
            <AdBanner />

            {fortune.omen && (
              <div className={`dream-omen omen-${fortune.omen === '길몽' ? 'good' : fortune.omen === '흉몽' ? 'bad' : 'caution'}`}>
                <span className="omen-badge">
                  {fortune.omen === '길몽' ? '🎉 길몽' : fortune.omen === '흉몽' ? '⚠️ 흉몽' : '🔔 주의'}
                </span>
              </div>
            )}

            <FortuneContent fortune={fortune} type="dream" />
          </>
        )}
      </div>

      <AdBanner />
    </div>
  );
}

export default DreamPage;
